import { useApp } from "@hooks/useApp";
import { useCore } from "@hooks/useCore";
import {
  type Badge,
  type ComponentProps,
  type GatsbyImageSourceProps,
} from "@ts/components";
import { ProductVideo } from "@ts/sanity";
import React, { FC, useLayoutEffect, useRef, useState } from "react";
import SwiperCore from "swiper";

export type ImagesInputProps = ComponentProps & {
  badge?: Badge;
  images?: GatsbyImageSourceProps[];
  video?: ProductVideo;
  onSlideChange: (index: number) => void;
};

export type ImagesOutputProps = Omit<ImagesInputProps, "images"> & {
  height?: number;
  images?: (GatsbyImageSourceProps & { isCoverImage?: boolean })[];
  isTouchDevice?: boolean;
  loaded?: boolean;
  onImagesReady?: () => void;
  onSlideChange?: (swiper: SwiperCore) => void;
  onThumbsSwiperInit?: (swiper: SwiperCore) => void;
  onThumbnailClick?: (index: number) => () => void;
  onVideoPlay?: () => void;
  onSwiperInit?: (swiper: SwiperCore) => void;
  playing?: boolean;
  slideIndex?: number;
  thumbsSwiper?: SwiperCore;
};

export const withImages =
  (Component: FC<ImagesOutputProps>) =>
  ({
    name = "Images",
    images,
    video,
    badge,
    onSlideChange,
  }: ImagesInputProps) => {
    const {
      config: {
        settings: {
          keys: { product_images_cover_image },
        },
      },
    } = useApp();
    const {
      helpers: { isBrowser },
    } = useCore();
    const [playing, setPlaying] = useState(false);
    const [slideIndex, setSlideIndex] = useState(0);
    const [height, setHeight] = useState<number | undefined>();
    const [swiper, setSwiper] = useState<SwiperCore | undefined>();
    const [loaded, setLoaded] = useState<boolean>(false);
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | undefined>();
    const containerRef = useRef<HTMLDivElement>();
    const isTouchDevice =
      isBrowser && !!(window?.ontouchstart || navigator?.maxTouchPoints > 0);
    const mappedImages = images?.map((image) => ({
      ...image,
      isCoverImage:
        !image?.url && image?.src?.indexOf(product_images_cover_image) !== -1,
    }));

    const handleThumbnailClick = (index: number) => () => {
      setSlideIndex(index);
      swiper.slideTo(index);
    };

    const handleSlideChange = ({ activeIndex }: SwiperCore) => {
      setSlideIndex(activeIndex);
      if (onSlideChange) {
        onSlideChange(activeIndex);
      }
    };

    const handleVideoPlaying = () => {
      setPlaying((prevState) => !prevState);
    };

    const handleSwiperInit = (swiperInstance: SwiperCore) => {
      if (onSlideChange) {
        onSlideChange(0);
      }
      setSwiper(swiperInstance);
    };

    const handleThumbsSwiperInit = (swiperInstance: SwiperCore) => {
      setThumbsSwiper(swiperInstance);
    };

    const handleResize = () => {
      if (!containerRef?.current?.getBoundingClientRect) {
        return;
      }
      const rect = containerRef.current.getBoundingClientRect();
      setHeight(rect.height);
    };

    const handleImagesReady = () => {
      setLoaded(true);
    };

    useLayoutEffect(() => {
      if (!containerRef?.current) {
        return;
      }
      handleResize();
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, [containerRef]);

    Component.displayName = name;

    if (!images?.length) {
      return null;
    }

    return (
      <Component
        badge={badge}
        height={height}
        images={mappedImages}
        isTouchDevice={isTouchDevice}
        loaded={loaded}
        onImagesReady={handleImagesReady}
        onSlideChange={handleSlideChange}
        onSwiperInit={handleSwiperInit}
        onThumbnailClick={handleThumbnailClick}
        onThumbsSwiperInit={handleThumbsSwiperInit}
        onVideoPlay={handleVideoPlaying}
        playing={playing}
        ref={containerRef}
        slideIndex={slideIndex}
        thumbsSwiper={thumbsSwiper}
        video={video}
      />
    );
  };
